import React, { useState, useEffect, Component } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../services/apiService";
import { useCarContext } from "../CarContext";
import { Loading } from "../components/Loading";
import "./PlaceBids.css";
import { Button, ImageWithFallback } from "../components/Utilities";
import imgTop from "../assets/imgTop.png";
import imgBottom from "../assets/imgBottom.png";
import locCard from "../assets/locCard.svg";
import rc_yes from "../assets/rc-yes.svg";
import rc_no from "../assets/rc-no.svg";
import wrong from "../assets/wrong.png"; // only cancel icon
import { LiveTimer } from "../components/CardLayout";
import { formatOwnership } from "../DealerHome";
import analyticsService from "../../services/analyticsService";
import { getUserTrackingInfo } from "../../utils/userTracking";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import { ToastContainer, toast } from "react-toastify";

/* ------------------------------------------------------------------ */
/* -------------------------- Error Boundary -------------------------- */
/* ------------------------------------------------------------------ */
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h3>Something went wrong</h3>
          <p>{this.state.error?.message || "An unexpected error occurred."}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ------------------------------------------------------------------ */
/* -------------------------- Confirm Modal -------------------------- */
/* ------------------------------------------------------------------ */
const ConfirmBidModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay confirm-bid-modal-overlay">
      <div
        className="modal-content confirm-bid-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Confirm Bid</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <p>
          My team physically inspected this car and I confirm the bid placed
          after verification of all aspects of the purchase.
        </p>
        <Button
          variant="primary"
          onClick={() => {
            onConfirm();
            onClose();
          }}
        >
          Yes, Confirm Bid
        </Button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* -------------------------- Time Formatter ------------------------- */
/* ------------------------------------------------------------------ */
const formatTimeLeft = (endDateStr) => {
  if (!endDateStr) return "N/A";
  const end = new Date(endDateStr);
  if (isNaN(end)) return "N/A";
  const diff = end - Date.now();
  if (diff <= 0) return "Expired";

  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  if (d) return `${d}D:${h.toString().padStart(2, "0")}H`;
  if (h) return `${h}H:${m.toString().padStart(2, "0")}M`;
  if (m) return `${m}M:${sec.toString().padStart(2, "0")}S`;
  return `${sec}S`;
};

/* ------------------------------------------------------------------ */
/* --------------------------- Main Component ------------------------ */
/* ------------------------------------------------------------------ */
export default function PlaceBids() {
  const navigate = useNavigate();
  const { placeBidsCount, setPlaceBidsCount } = useCarContext();

  const [cars, setCars] = useState([]);
  const [bidStates, setBidStates] = useState({});
  const [modal, setModal] = useState({ isOpen: false, carId: null });
  const [loading, setLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [lastHash, setLastHash] = useState(null);
  const [carId, setCarId] = useState(null);

  // persistent counters
  const [totalBidsPlaced, setTotalBidsPlaced] = useState(() => {
    const s = localStorage.getItem("totalBidsPlaced");
    return s ? +s : 0;
  });
  const [editCounts, setEditCounts] = useState(() => {
    const s = localStorage.getItem("editCounts");
    return s ? JSON.parse(s) : {};
  });
  const maxBids = 5;

  useEffect(
    () => localStorage.setItem("totalBidsPlaced", totalBidsPlaced),
    [totalBidsPlaced]
  );
  useEffect(
    () => localStorage.setItem("editCounts", JSON.stringify(editCounts)),
    [editCounts]
  );

  // error toast


  /* -------------------------- Fetch Cars -------------------------- */
  const fetchCars = async () => {
    try {
      setLoading(true);

      setShowError(false);
      const { data } = await apiService.getDealerPlaceBidsCars({});

      if (!data) {
        toast.error("No data returned from the server.");
        setPlaceBidsCount(0);
        return;
      }

      const yardCars = data.yardGroups?.flatMap((g) => g.cars || []) || [];
      const hash = JSON.stringify(yardCars.map((c) => c.id).sort());

      if (lastHash && lastHash !== hash) setPlaceBidsCount(yardCars.length);
      setLastHash(hash);

      const valid = yardCars
        .filter((c) => c && typeof c === "object")
        .map((c) => ({
          id: c.id || `car-${Math.random().toString(36).substr(2, 9)}`,
          brand: c.make || "Unknown",
          model: c.model || "Unknown",
          rto: c.regno || "N/A",
          fuel: c.fueltype || "Unknown",
          transmission: c.transmission || "Manual",
          year: +c.year || 0,
          km: +c.kmsrun || 0,
          startDate: c.auction_start_date,
          endDate: c.auction_end_date,
          end_date: c.auction_end_date || null,
          location: c.yard || c.city || "Unknown",
          city: c.city || "Unknown",
          rc: ["YES", "Y", "AVAILABLE"].includes(
            (c.rcstatus || "").toUpperCase()
          ),
          owner: formatOwnership(c.ownership),
          yard: c.yard || "Unknown",
          price: +c.latestBidValue || +c.highestBid || +c.currentBid || 0,
          flowEntityId: c.flowEntityId || c.id,
          auctionId: c.auctionId || "",
          bids: c.bids || [],
          evaluationImages: c.evaluationImages || [],
          car_image_urls: c.car_image_urls || [],
        }));

      setCars(valid);
      setPlaceBidsCount(valid.length);

      const initStates = valid.reduce((acc, car) => {
        acc[car.id] = {
          editMode: false,
          bidAmount: car.price,
          currentBid: car.price,
          previousBid: car.price,
          bidsPlaced: car.bids.length,
          timeLeft: car.end_date ? formatTimeLeft(car.end_date) : "N/A",
        };
        return acc;
      }, {});
      setBidStates(initStates);

      const initCounts = valid.reduce((acc, car) => {
        acc[car.id] = car.bids.length;
        return acc;
      }, {});
      setEditCounts((p) => ({ ...p, ...initCounts }));
    } catch (e) {
      toast.error("Failed to load cars for bidding.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars();
  }, [navigate, setPlaceBidsCount]);

  /* --------------------- Enter Edit Mode (click card or price) -------------------- */
  const enterEdit = (carId, e) => {
    e.stopPropagation();
    setCarId(carId); // stop card navigation
    setBidStates((prev) => ({
      ...prev,
      [carId]: {
        ...prev[carId],
        editMode: true,
        bidAmount: prev[carId].currentBid || 0, // pre-fill +1
      },
    }));
  };

  /* --------------------- Cancel Edit (wrong icon) -------------------- */
  const cancelEdit = (carId, e) => {
    e.stopPropagation();
    setBidStates((prev) => ({
      ...prev,
      [carId]: {
        ...prev[carId],
        editMode: false,
        bidAmount: prev[carId].currentBid,
      },
    }));
  };

  /* --------------------- Input Change (dynamic button) -------------------- */
  const onInputChange = (carId, value) => {
    setBidStates((prev) => ({
      ...prev,
      [carId]: { ...prev[carId], bidAmount: value },
    }));
  };

  /* --------------------- Submit (validation → modal) -------------------- */
  const submitBid = (carId, e) => {
    e.stopPropagation();
    const st = bidStates[carId];
    const amt = parseFloat(st.bidAmount);
    if (isNaN(amt) || amt <= st.previousBid) {
      toast.error("Bid amount must be greater than previous bid.");
      return;
    }
    setModal({ isOpen: true, carId });
  };

  /* --------------------- Confirm (API) -------------------- */
  const confirmBid = async (carId) => {
    const st = bidStates[carId];
    const car = cars.find((c) => c.id === carId);
    const amt = parseFloat(st.bidAmount);
    if (!car) return;

    try {
      await apiService.placeBid({
        carId,
        flowEntityId: car.flowEntityId,
        bidAmount: amt,
      });

      const userInfo = getUserTrackingInfo(
        {
          uid: localStorage.getItem("uid"),
          name: localStorage.getItem("name"),
          email: localStorage.getItem("email"),
          organization: localStorage.getItem("organization"),
        },
        ["orguserdealer"],
        "orguserdealer"
      );
      analyticsService.trackBidPlacementEnhanced(
        {
          carMake: car.brand,
          carModel: car.model,
          carYear: car.year,
          supplierName: car.supplierName || car.supplier || "Unknown",
          carId: car.id,
          registrationNumber: car.rto,
          price: car.price,
          location: car.city,
          bidAmount: amt,
          totalBidsPlaced: totalBidsPlaced + 1,
          maxBids,
        },
        userInfo
      );

      setTotalBidsPlaced((p) => p + 1);
      setEditCounts((p) => ({ ...p, [carId]: (p[carId] || 0) + 1 }));

      setBidStates((prev) => ({
        ...prev,
        [carId]: {
          ...prev[carId],
          currentBid: amt,
          previousBid: amt,
          bidsPlaced: (prev[carId].bidsPlaced || 0) + 1,
          editMode: false,
          bidAmount: amt,
        },
      }));
      toast.success(`Bid of ₹${amt.toLocaleString()} placed successfully!`, {
  autoClose: 3000,
});
    } catch (e) {
      const msg = e.response?.data?.error || e.message || "Failed to place bid. Please try again.";
      toast.error(msg);
    } finally {
      setModal({ isOpen: false, carId: null });
    }
  };

  /* --------------------- Button Text Logic -------------------- */
  const getButtonText = (carId) => {
    const st = bidStates[carId];
    if (!st) return "Place Bid";

    if (st.editMode) {
      // const entered = parseFloat(st.bidAmount);
      // if (!isNaN(entered) && entered > st.previousBid) {
      //   return "Confirm Bid";
      // }
      return "Confirm Bid";
    }

    return st.bidsPlaced === 0 ? "Place Bid" : "Edit Bid";
  };
  /* --------------------- Render --------------------- */
  return (
    <ErrorBoundary>
      <div className="placeBids" onClick={(e) => cancelEdit(carId, e)}>
        <div className="container">
          <div className="content-wrapper">
            <div className="main-content">
              <div className="car-grid-container">
                {loading && <Loading />}

              

                {!loading && !showError && cars.length === 0 && (
                  <div className="no-results">
                    <h3>No cars found</h3>
                    <p>No cars available to bid on.</p>
                    <Button
                      className="see-more"
                      variant="primary"
                      onClick={() => navigate("/DealerHome")}
                    >
                      Back to Home
                    </Button>
                  </div>
                )}

                {!loading && !showError && cars.length > 0 && (
                  <div className="car-grid">
                    {cars.map((car) => {
                      const st = bidStates[car.id] || {};

                      const disabled =
                        (st.bidsPlaced || 0) >= maxBids ||
                        st.timeLeft === "Expired";

                      const submitDisabled =
                        disabled ||
                        (st.editMode &&
                          (!st.bidAmount ||
                            parseFloat(st.bidAmount) <= st.previousBid));
                      return (
                        <div
                          className={`car-card ${car.isSold ? "sold" : ""}`}
                          key={car.id}
                          onClick={(e) => {
                            cancelEdit(car.id, e);
                            e.stopPropagation();
                            navigate(`/car-details/${car.id}`);
                          }}
                          // Click anywhere on card → open input (except when already editing)
                        >
                          {/* SOLD overlay */}
                          {car.isSold && (
                            <div className="sold-overlay">
                              <div className="sold-badge">
                                <span className="sold-text">SOLD</span>
                              </div>
                            </div>
                          )}

                          {/* Images */}
                          <div className="car-image-wrapper">
                            <img src={imgTop} className="imgTop" alt="" />
                            <img src={imgBottom} className="imgBottom" alt="" />
                            {car.evaluationImages?.length > 0 ? (
                              <Swiper
                                modules={[Pagination, Autoplay]}
                                spaceBetween={0}
                                slidesPerView={1}
                                loop
                                autoplay={{
                                  delay: 2500,
                                  disableOnInteraction: false,
                                }}
                                pagination={{ clickable: true }}
                                className="car-image-carousel"
                                allowTouchMove
                              >
                                {car.evaluationImages.map((img, i) => (
                                  <SwiperSlide key={img.id || i}>
                                    <div className="carousel-image-container">
                                      <ImageWithFallback
                                        src={img.url}
                                        alt={`${car.brand} ${car.model} - ${
                                          img.angle || i + 1
                                        }`}
                                        className="car-image"
                                      />
                                    </div>
                                  </SwiperSlide>
                                ))}
                              </Swiper>
                            ) : (
                              <ImageWithFallback
                                src={
                                  car.car_image_urls?.[0]?.url ||
                                  car.car_image_urls?.[0] ||
                                  "https://via.placeholder.com/150"
                                }
                                alt="Car"
                                className="car-image"
                              />
                            )}
                            <LiveTimer
                              startDateStr={car.startDate}
                              endDateStr={car.endDate}
                            />
                            <div className="car-location">
                              <ImageWithFallback src={locCard} alt="Location" />
                           {   console.log("lohith",car.location)}
                              
                              <p className="car-location-name">
                                {car.location.charAt(0).toUpperCase() +
                                  car.location.slice(1)}
                              </p>
                            </div>
                          </div>

                          {/* Details */}
                          <div className="car-details">
                            <div className="car-info">
                              <h3 className="car-title">
                                {car.year} {car.brand} {car.model}
                              </h3>
                              <div className="car-spec-container">
                                <div className="car-specs">
                                  <span className="distance">
                                    {car.rc ? (
                                      <img src={rc_yes} alt="" className="rc" />
                                    ) : (
                                      <img src={rc_no} alt="" className="rc" />
                                    )}
                                    {car.km.toLocaleString()} kms
                                  </span>
                                  <span className="car-fuel">
                                    {" "}
                                    • {car.fuel} • {car.transmission}
                                  </span>
                                </div>
                                <div className="car-tags">
                                  {car.rc ? (
                                    <img
                                      src={rc_yes}
                                      alt="RC Yes"
                                      className="rc"
                                    />
                                  ) : (
                                    <img
                                      src={rc_no}
                                      alt="RC No"
                                      className="rc"
                                    />
                                  )}
                                  <span className="rto-tag">{car.rto}</span>
                                  <span className="owner-tag">{car.owner}</span>
                                </div>
                                   <div className="phone-car-location">
                              <ImageWithFallback src={locCard} alt="Location" />
                              <p className="car-location-name">
                                {car.location}
                              </p>
                            </div>
                              </div>
                            </div>

                            {/* ==== Desktop Bottom ==== */}
                            <div className="web-bottom">
                              <div
                                className="car-price"
                                onClick={(e) => {
                                  if (!disabled) {
                                    !st.editMode && enterEdit(car.id, e);
                                  }
                                }}
                              >
                                {st.editMode ? (
                                  <div className="bid-input-wrapper">
                                    <input
                                      type="number"
                                      className="bid-input"
                                      value={st.bidAmount || ""}
                                      onChange={(e) =>
                                        onInputChange(car.id, e.target.value)
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCarId(car.id);
                                      }} // prevent card click
                                      min={(st.previousBid || car.price) + 1}
                                      placeholder="Enter bid amount"
                                      autoFocus
                                      disabled={disabled}
                                    />
                                    {/* Cancel icon */}
                                    <img
                                      className="wrong-icon"
                                      onClick={(e) => cancelEdit(car.id, e)}
                                      style={{
                                        cursor: "pointer",
                                        marginLeft: "8px",
                                      }}
                                      src={wrong}
                                      alt="Cancel"
                                    />
                                  </div>
                                ) : (
                                  <span
                                    onClick={(e) => enterEdit(car.id, e)} // click price → edit
                                    style={{ cursor: "pointer" }}
                                  >
                                    ₹
                                    {(
                                      st.currentBid || car.price
                                    ).toLocaleString()}
                                  </span>
                                )}
                              </div>

                              <div className="bids-placed">
                                Bids placed: {st.bidsPlaced || 0}/{maxBids}
                              </div>

                              <div className="car-actions">
                                <Button
                                  variant="primary"
                                  onClick={(e) =>
                                    st.editMode
                                      ? submitBid(car.id, e)
                                      : enterEdit(car.id, e)
                                  }
                                  disabled={
                                    disabled ||
                                    (st.editMode &&
                                      (!st.bidAmount ||
                                        parseFloat(st.bidAmount) <=
                                          st.previousBid))
                                  }
                                >
                                  {getButtonText(car.id)}
                                </Button>
                              </div>
                            </div>
                          </div>
                          {/* ==== Mobile Bottom ==== */}
                          <div className="mobile-bottom">
                            <div>
                              <div
                                className="car-price"
                                onClick={(e) => {
                                  if (!disabled) {
                                    !st.editMode && enterEdit(car.id, e);
                                  }
                                }}
                              >
                                {st.editMode ? (
                                  <div className="bid-input-wrapper">
                                    <input
                                      type="number"
                                      className="bid-input"
                                      value={st.bidAmount || ""}
                                      onChange={(e) =>
                                        onInputChange(car.id, e.target.value)
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      min={(st.previousBid || car.price) + 1}
                                      placeholder="Enter bid amount"
                                      autoFocus
                                    />
                                    <img
                                      className="wrong-icon"
                                      onClick={(e) => cancelEdit(car.id, e)}
                                      style={{
                                        cursor: "pointer",
                                        marginLeft: "8px",
                                      }}
                                      src={wrong}
                                      alt="Cancel"
                                      width="10rem"
                                    />
                                  </div>
                                ) : (
                                  <span
                                    onClick={(e) => enterEdit(car.id, e)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    ₹
                                    {(
                                      st.currentBid || car.price
                                    ).toLocaleString()}
                                  </span>
                                )}
                              </div>

                              <div className="bids-placed">
                                Bids placed: {st.bidsPlaced || 0}/{maxBids}
                              </div>
                            </div>

                            <div className="car-actions">
                              <Button
                                className="phone-btn-full"
                                variant="primary"
                                onClick={(e) =>
                                  st.editMode
                                    ? submitBid(car.id, e)
                                    : enterEdit(car.id, e)
                                }
                                disabled={
                                  disabled ||
                                  (st.editMode &&
                                    (!st.bidAmount ||
                                      parseFloat(st.bidAmount) <=
                                        st.previousBid))
                                }
                              >
                                {getButtonText(car.id)}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Confirm Modal */}
        <ConfirmBidModal
          isOpen={modal.isOpen}
          onClose={() => setModal({ isOpen: false, carId: null })}
          onConfirm={() => modal.carId && confirmBid(modal.carId)}
        />
      </div>
      <ToastContainer
  position="top-right"
  autoClose={3000}
  hideProgressBar={false}
  newestOnTop={false}
  closeOnClick
  rtl={false}
  pauseOnFocusLoss
  draggable
  pauseOnHover
/>
    </ErrorBoundary>
  );
}
